import { PARTY_SIZE, partyColourOf, type ColourRoleName } from "odyssey-shared";
import type { FlagState } from "../../ui/kit/index.ts";
import { flagOf } from "../hud/store.ts";
import { say } from "../../words.ts";
import type { MenuEntry } from "../../ui/kit/index.ts";
import { sameName, type PartyMember, type PartyName } from "./members.ts";
import type { PartySnapshot } from "./store.ts";

/**
 * The party frame's rows and the player menu's entries (ADR-101 decisions 3, 9 and 23), as data the panels draw: the
 * rows from the roster the gateway sent, the entries from what the player may do in it.
 */

/**
 * Where a member stands, as far as the roster says: with the player on its map, on another map, or offline.
 */
export type MemberPlace = "here" | "elsewhere" | "offline";

/**
 * What a row shows under the name for a member on the player's map: health with the flag mark, Dead, or nothing while
 * no frame has named the member yet (a member on the player's map is drawn with its name alone until one does).
 */
export type RowVitals =
  | { readonly kind: "none" }
  | { readonly kind: "dead"; readonly flag: FlagState; readonly locked: boolean }
  | { readonly kind: "alive"; readonly flag: FlagState; readonly locked: boolean; readonly health: number };

/**
 * One row of the frame. `colour` is the colour role of the member's map mark (`partyColourOf` of the place).
 */
export interface FrameRow {
  readonly colour: ColourRoleName;
  readonly vitals: RowVitals;
  readonly place: number;
  readonly name: string;
  readonly fullName: string;
  readonly leader: boolean;
  readonly self: boolean;
  readonly where: MemberPlace;
}

/**
 * Where a member stands.
 *
 * @param member The member.
 * @param self The player's own seat, or null.
 * @returns Offline for an offline seat, elsewhere for an online member on another map than the player's, else here.
 */
export function placeOf(member: PartyMember, self: PartyMember | null): MemberPlace {
  if (!member.online) {
    return "offline";
  }
  return self !== null && self.online && self.map !== "" && member.map !== self.map ? "elsewhere" : "here";
}

/**
 * What a member's row shows of its vitals: nothing for an offline member or one on another map, and for one on the
 * player's map what the last frame (or, for the player's own seat, the HUD) holds.
 *
 * @param snapshot The party.
 * @param member The member.
 * @returns The vitals to draw.
 */
function vitalsOf(snapshot: PartySnapshot, member: PartyMember): RowVitals {
  const held = snapshot.vitals.get(member.place);
  if (placeOf(member, snapshot.self) !== "here") {
    return { kind: "none" };
  }
  if (held === undefined) {
    return { kind: "none" };
  }
  const { state, locked } = flagOf(held.flag);
  return held.dead ? { kind: "dead", flag: state, locked } : { kind: "alive", flag: state, locked, health: held.health };
}

/**
 * The frame's rows, one for every seat in place order, the player's included.
 *
 * @param snapshot The party.
 * @returns The rows; none while the party seats fewer than two.
 */
export function frameRows(snapshot: PartySnapshot): readonly FrameRow[] {
  return snapshot.members.map((member) => ({
    colour: partyColourOf(member.place),
    vitals: vitalsOf(snapshot, member),
    place: member.place,
    name: member.firstName,
    fullName: `${member.firstName} ${member.familyName}`.trim(),
    leader: member.leader,
    self: snapshot.self !== null && member.place === snapshot.self.place,
    where: placeOf(member, snapshot.self),
  }));
}

/**
 * The count line at the frame's foot: "4/6", and "6/6 · full" at six.
 *
 * @param count The seats held.
 * @returns The line.
 */
export function countLine(count: number): string {
  return say(count >= PARTY_SIZE ? "party.frames.countFull" : "party.frames.count", { count: String(count), max: String(PARTY_SIZE) });
}

/**
 * What a player menu entry does.
 */
export type PartyChoice = "invite" | "leave" | "remove" | "promote" | "grant";

/**
 * The entries of the menu for a character: Invite to party for a character outside the player's party (disabled, with
 * its reason, while the party is full or the player may not invite); Leave party on the player's own seat; and on
 * another member, for the leader, Make leader (disabled while that member is offline), Can invite (a toggle showing
 * whether it may) and, after a divider, Remove from party. Another member's menu is empty for a member who is not the
 * leader.
 *
 * @param snapshot The party.
 * @param subject The character the menu is for.
 * @returns The entries.
 */
export function menuEntries(snapshot: PartySnapshot, subject: PartyName): readonly MenuEntry<PartyChoice>[] {
  const seat = snapshot.members.find((member) => sameName(member, subject)) ?? null;
  const self = snapshot.self;
  if (seat !== null) {
    if (self !== null && seat.place === self.place) {
      return [{ value: "leave", label: say("party.menu.leave"), glyph: "logout" }];
    }
    if (self === null || !self.leader) {
      return [];
    }
    return [
      { value: "promote", label: say("party.menu.promote"), glyph: "star", disabled: !seat.online, ...(seat.online ? {} : { why: say("party.menu.offline") }) },
      { value: "grant", label: say("party.menu.grant"), glyph: "users", checked: seat.mayInvite },
      { value: "remove", label: say("party.menu.remove"), glyph: "close-small", danger: true, divided: true },
    ];
  }
  const full = snapshot.members.length >= PARTY_SIZE;
  const mayNot = self !== null && !self.leader && !self.mayInvite;
  const why = full ? say("party.menu.full", { max: String(PARTY_SIZE) }) : mayNot ? say("party.menu.leaderOnly") : undefined;
  return [{ value: "invite", label: say("party.menu.invite"), glyph: "users", disabled: why !== undefined, ...(why === undefined ? {} : { why }) }];
}
